/**
 * Organic tome silhouettes (lane N, 2026-10-07): deterministic, size-aware
 * outlines for parchment, covers, folios and entries. Pure geometry: no DOM.
 *
 * - Deterministic: every outline is drawn from a seeded generator, so one
 *   window keeps one silhouette across renders and reopenings.
 * - Size-aware: edge detail is placed by distance in CSS pixels, never by
 *   percentage, so a tall window gets more deckle, not stretched deckle. Each
 *   edge is generated from its top or left end, so a page that grows keeps the
 *   silhouette it already had and only extends it.
 * - Inward only: the outline never leaves the element's box, and the rule
 *   (`rule()`) is inset past the deepest bite, so ruled borders and text sit
 *   on whole paper.
 */

export type TomeEdge = 'clean' | 'deckled' | 'torn';
export type TomeCorners = 'square' | 'round' | 'chamfer' | 'notched';

export interface TomeShapeSpec {
  edge: TomeEdge;
  corners: TomeCorners;
  /** Corner size in px (the cut, scoop or radius). */
  corner: number;
  /** Deepest inward edge bite in px at roughness 1, for `deckled`; `torn` multiplies it. */
  deckle: number;
  /** Slow inward bowing of each edge in px at roughness 1, so long edges are never ruler-straight. */
  warp?: number;
  /** Multiplier on the bite depth; 0 draws a clean edge. */
  roughness: number;
  seed: string | number;
  /** Seeds corner proportions separately, so nested layers (cover and page) keep nested corners. Defaults to `seed`. */
  cornerSeed?: string | number;
}

/** FNV-1a: a stable 32-bit hash for string seeds. */
export function hashSeed(seed: string | number): number {
  const text = String(seed);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32: small, fast, good enough for ornament. Returns floats in [0, 1). */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const round = (value: number) => Math.round(value * 10) / 10;

/** How deep the outline can bite into the element, in px (for insetting rules and content). */
export function maxBite(spec: Pick<TomeShapeSpec, 'edge' | 'deckle' | 'roughness' | 'warp'>): number {
  if (spec.edge === 'clean') return 0;
  const torn = spec.edge === 'torn';
  const rough = Math.max(0, spec.roughness);
  return spec.deckle * rough * (torn ? 2.6 * 1.6 : 1) + (spec.warp ?? 0) * rough;
}

/**
 * Inward offsets along one edge of `length` px, between corner margins. Returns
 * [distance, depth] pairs ordered by distance from the edge's start.
 */
function edgeProfile(length: number, startMargin: number, endMargin: number, spec: TomeShapeSpec, salt: number): Array<[number, number]> {
  const usable = length - startMargin - endMargin;
  if (spec.edge === 'clean' || spec.roughness <= 0 || usable < 8) return [];
  const random = seededRandom(hashSeed(spec.seed) ^ Math.imul(salt + 1, 0x9e3779b1));
  const torn = spec.edge === 'torn';
  const depth = spec.deckle * spec.roughness * (torn ? 2.6 : 1);
  const baseStep = torn ? 13 : 7;
  // A slow wave so the deckle swells and settles rather than buzzing evenly.
  const wavelength = 60 + random() * 70;
  const phase = random() * Math.PI * 2;
  // The bow: two long waves, inward only, eased to nothing near the corners.
  const warp = (spec.warp ?? 0) * spec.roughness;
  const bowA = 180 + random() * 160; const bowB = 90 + random() * 60;
  const bowPhaseA = random() * Math.PI * 2; const bowPhaseB = random() * Math.PI * 2;
  const points: Array<[number, number]> = [];
  let along = startMargin + baseStep * (0.4 + random() * 0.6);
  let biteLeft = 0;
  while (along < length - endMargin - 3) {
    const swell = 0.5 + 0.5 * Math.sin(along / wavelength * Math.PI * 2 + phase);
    let bite = depth * (0.25 + 0.45 * swell + 0.3 * random());
    if (torn) {
      if (biteLeft > 0) { bite = depth * (1.1 + 0.5 * random()); biteLeft--; }
      else if (random() < 0.07) biteLeft = 1 + Math.floor(random() * 2);
    }
    // Ease to the corner margins so the corner joins the edge cleanly.
    const fromStart = along - startMargin;
    const fromEnd = length - endMargin - along;
    const ease = Math.min(1, fromStart / (baseStep * 1.5), fromEnd / (baseStep * 1.5));
    const t = Math.min(1, fromStart / 48, fromEnd / 48);
    const bowEase = t * t * (3 - 2 * t);
    const bow = warp * bowEase * (0.5 + 0.25 * Math.sin(along / bowA * Math.PI * 2 + bowPhaseA) + 0.25 * Math.sin(along / bowB * Math.PI * 2 + bowPhaseB));
    points.push([along, Math.min(bite * ease + bow, maxBite(spec))]);
    along += baseStep * (torn ? 0.45 + random() * 1.3 : 0.6 + random() * 0.8);
  }
  return points;
}

type Corner = 'tl' | 'tr' | 'br' | 'bl';

function cornerSizes(spec: TomeShapeSpec, width: number, height: number): Record<Corner, number> {
  const random = seededRandom(hashSeed(spec.cornerSeed ?? spec.seed) ^ 0x51ed270b);
  const cap = Math.max(0, Math.min(width, height) / 3);
  const size = () => spec.corners === 'square' ? 0 : Math.min(cap, spec.corner * (0.8 + random() * 0.4));
  return { tl: size(), tr: size(), br: size(), bl: size() };
}

/**
 * Corner segment from the end of one edge to the start of the next.
 * (ax, ay) is where the corner begins, (bx, by) where it ends and (kx, ky) is
 * the box corner; (ix, iy) points into the element.
 */
function cornerSegment(corners: TomeCorners, size: number, k: [number, number], b: [number, number], inward: [number, number]): string {
  if (size <= 0 || corners === 'square') return `L${round(k[0])} ${round(k[1])}`;
  if (corners === 'chamfer') return `L${round(b[0])} ${round(b[1])}`;
  if (corners === 'round') return `Q${round(k[0])} ${round(k[1])} ${round(b[0])} ${round(b[1])}`;
  // notched: a scoop bowed into the page, like a cut ticket corner.
  const control: [number, number] = [k[0] + inward[0] * size, k[1] + inward[1] * size];
  return `Q${round(control[0])} ${round(control[1])} ${round(b[0])} ${round(b[1])}`;
}

/** The outline of a `width` × `height` element, as an SVG/CSS path in CSS pixels. */
export function tomeOutline(width: number, height: number, spec: TomeShapeSpec): string {
  if (!(width > 0) || !(height > 0)) return '';
  const w = width; const h = height;
  const c = cornerSizes(spec, w, h);
  const top = edgeProfile(w, c.tl, c.tr, spec, 0);
  const right = edgeProfile(h, c.tr, c.br, spec, 1);
  const bottom = edgeProfile(w, c.bl, c.br, spec, 2);
  const left = edgeProfile(h, c.tl, c.bl, spec, 3);
  const parts: string[] = [`M${round(c.tl)} 0`];
  for (const [x, d] of top) parts.push(`L${round(x)} ${round(d)}`);
  parts.push(`L${round(w - c.tr)} 0`);
  parts.push(cornerSegment(spec.corners, c.tr, [w, 0], [w, c.tr], [-1, 1]));
  for (const [y, d] of right) parts.push(`L${round(w - d)} ${round(y)}`);
  parts.push(`L${round(w)} ${round(h - c.br)}`);
  parts.push(cornerSegment(spec.corners, c.br, [w, h], [w - c.br, h], [-1, -1]));
  for (let i = bottom.length - 1; i >= 0; i--) parts.push(`L${round(bottom[i][0])} ${round(h - bottom[i][1])}`);
  parts.push(`L${round(c.bl)} ${round(h)}`);
  parts.push(cornerSegment(spec.corners, c.bl, [0, h], [0, h - c.bl], [1, -1]));
  for (let i = left.length - 1; i >= 0; i--) parts.push(`L${round(left[i][1])} ${round(left[i][0])}`);
  parts.push(`L0 ${round(c.tl)}`);
  parts.push(cornerSegment(spec.corners, c.tl, [0, 0], [c.tl, 0], [1, 1]));
  parts.push('Z');
  return parts.join('');
}

/**
 * A clean ruled line inset `inset` px inside the outline (past its deepest
 * bite), with the same corner style scaled down: the gilt rule of a page.
 */
export function tomeRule(width: number, height: number, spec: TomeShapeSpec, inset: number): string {
  const i = inset + maxBite(spec);
  const w = width - 2 * i; const h = height - 2 * i;
  if (!(w > 4) || !(h > 4)) return '';
  const c = cornerSizes(spec, width, height);
  const shrink = (size: number) => Math.max(spec.corners === 'square' ? 0 : 2, size - i * 0.7);
  const tl = shrink(c.tl); const tr = shrink(c.tr); const br = shrink(c.br); const bl = shrink(c.bl);
  const x0 = i; const y0 = i; const x1 = i + w; const y1 = i + h;
  return [
    `M${round(x0 + tl)} ${round(y0)}`, `L${round(x1 - tr)} ${round(y0)}`,
    cornerSegment(spec.corners, tr, [x1, y0], [x1, y0 + tr], [-1, 1]),
    `L${round(x1)} ${round(y1 - br)}`,
    cornerSegment(spec.corners, br, [x1, y1], [x1 - br, y1], [-1, -1]),
    `L${round(x0 + bl)} ${round(y1)}`,
    cornerSegment(spec.corners, bl, [x0, y1], [x0, y1 - bl], [1, -1]),
    `L${round(x0)} ${round(y0 + tl)}`,
    cornerSegment(spec.corners, tl, [x0, y0], [x0 + tl, y0], [1, 1]),
    'Z',
  ].join('');
}

export interface TomeStain { cx: number; cy: number; rx: number; ry: number; opacity: number }

/** A few faint, seeded tide-mark stains for aged paper, placed away from the centre of the page. */
export function tomeStains(width: number, height: number, seed: string | number, count: number): TomeStain[] {
  const random = seededRandom(hashSeed(seed) ^ 0x2545f491);
  const stains: TomeStain[] = [];
  for (let i = 0; i < count; i++) {
    const edgeward = random() < 0.5 ? random() * 0.22 : 0.78 + random() * 0.22;
    const across = random();
    const horizontal = random() < 0.5;
    const r = Math.min(width, height) * (0.12 + random() * 0.14);
    stains.push({
      cx: round((horizontal ? across : edgeward) * width),
      cy: round((horizontal ? edgeward : across) * height),
      rx: round(r * (0.8 + random() * 0.6)), ry: round(r * (0.6 + random() * 0.5)),
      opacity: round((0.05 + random() * 0.06) * 100) / 100,
    });
  }
  return stains;
}

/** Index 1..4 for the hand-cut radius variants of small pieces (buttons, chips). */
export function tomeCutVariant(seed: string | number): 1 | 2 | 3 | 4 {
  return ((hashSeed(seed) % 4) + 1) as 1 | 2 | 3 | 4;
}
